import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  IconButton, Chip, CircularProgress, useTheme, alpha,
} from '@mui/material';
import { Add as AddIcon, LocationOn as LocationIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import venueLocationService from '../../../../services/venueLocationService';
import type { VenueLocation } from '../../../../types/venue';

const VenueLocationList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { venueId } = useVenue();
  const base = `/admin/venues/${venueId}`;

  const [locations, setLocations] = useState<VenueLocation[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const data = await venueLocationService.getLocations(venueId);
      setLocations(data);
    } catch (err) {
      console.error('Failed to load locations', err);
    } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  const handleDelete = async (e: React.MouseEvent, loc: VenueLocation) => {
    e.stopPropagation();
    if (!window.confirm(`Deactivate location "${loc.name}"?`)) return;
    try {
      await venueLocationService.deleteLocation(venueId, loc._id);
      fetchLocations();
    } catch (err) {
      console.error('Failed to delete location', err);
    }
  };

  const thStyle = {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    textTransform: 'uppercase' as const,
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <LocationIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6" fontWeight={600}>Locations</Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`${base}/locations/create`)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            New Location
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={thStyle}>Name</TableCell>
                <TableCell sx={thStyle}>Type</TableCell>
                <TableCell sx={thStyle}>Description</TableCell>
                <TableCell sx={thStyle}>Default</TableCell>
                <TableCell sx={thStyle}>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={32} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : locations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box sx={{ py: 6, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <LocationIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3 }} />
                      <Typography variant="body2">No locations yet. Add the first one.</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                locations.map(loc => (
                  <TableRow
                    key={loc._id}
                    hover
                    onClick={() => navigate(`${base}/locations/edit/${loc._id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{loc.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{loc.type}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{loc.description || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      {loc.isDefault && (
                        <Chip label="Default" size="small" color="primary" sx={{ fontSize: '0.7rem', height: 22 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={loc.active ? 'Active' : 'Inactive'}
                        size="small"
                        color={loc.active ? 'success' : 'default'}
                        sx={{ fontSize: '0.7rem', height: 22 }}
                      />
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      <IconButton size="small" onClick={() => navigate(`${base}/locations/edit/${loc._id}`)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={e => handleDelete(e, loc)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default VenueLocationList;
